import {
  CloseOutlined,
  FilterListOutlined,
  PlaceOutlined,
  SearchOutlined,
  SouthOutlined,
} from '@mui/icons-material'
import {
  Box,
  Checkbox,
  InputAdornment,
  Stack,
  useTheme,
  type AutocompleteRenderInputParams,
} from '@mui/material'
import { useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Autocomplete } from '@front/components/inputs/autocomplete/Autocomplete'
import { FilterItem } from '@front/components/inputs/filter-item/FilterItem'
import { StyledAutocompleteTextfield } from '@front/components/inputs/form-wrappers/FormWrappers.styled'
import { IconButton } from '@front/components/inputs/icon-button/IconButton'
import { TextField } from '@front/components/inputs/textfield/TextField'

import { ResultsContainer } from './results/ResultsContainer'
import { departments } from '../../constants/departments'
import { MoreFiltersDialog } from '../dialogs/MoreFiltersDialog'
import { SortingDialog } from '../dialogs/SortingDialog'
import { useJobAdsFiltersStorage } from '../hooks/useJobAdsFiltersStorage'
import type { JobAdsFormType } from '../../types/career.types'
import { SmallText } from '@front/components/data-display/typography/Typography'

type TabProps = {
  slug: string
}

export default function JobAdsTab({ slug }: TabProps) {
  const theme = useTheme()
  const { t } = useTranslation([
    'main.worker',
    'main.offers',
    'network.career_website',
  ])

  // filters dialogs
  const [sortDialogIsOpen, setSortDialogIsOpen] = useState<boolean>(false)
  const [moreFiltersDialogIsOpen, setMoreFiltersDialogIsOpen] =
    useState<boolean>(false)

  // get initial values from localStorage
  const { storageData, setStorageData, isReady } = useJobAdsFiltersStorage()

  const useFormMethods = useForm<JobAdsFormType>({
    defaultValues: storageData,
  })

  const departmentOptions = departments.map((el) => ({
    label: el.name,
    value: el.code_insee,
  }))

  const openSortDialog = () => {
    setSortDialogIsOpen(true)
  }
  const openMoreFiltersDialog = () => {
    setMoreFiltersDialogIsOpen(true)
  }

  const getMoreFiltersCounter = () => {
    return (
      (storageData.contractType?.length ? storageData.contractType.length : 0) +
      (storageData.contractDuration?.length
        ? storageData.contractDuration.length
        : 0) +
      (storageData.institutionType?.length
        ? storageData.institutionType.length
        : 0) +
      (storageData.yearsOfExperience ? 1 : 0)
    )
  }

  // The panel reads its values from the form, so both are cleared.
  const clearMoreFilters = () => {
    useFormMethods.setValue('contractType', undefined)
    useFormMethods.setValue('contractDuration', undefined)
    useFormMethods.setValue('institutionType', undefined)
    useFormMethods.setValue('yearsOfExperience', undefined)
    setStorageData({
      ...storageData,
      contractType: undefined,
      contractDuration: undefined,
      institutionType: undefined,
      yearsOfExperience: undefined,
    })
  }

  const storeInputChange = ({
    key,
    value,
  }: {
    key: string
    value?: string
  }) => {
    setStorageData({ ...storageData, [key]: value || undefined })
  }

  const updateFirstFormHydration = () => {
    Object.keys(storageData).forEach((key) => {
      const k = key as keyof JobAdsFormType
      useFormMethods.setValue(k, storageData[k])
    })
  }

  useEffect(() => {
    if (isReady) {
      updateFirstFormHydration()
    }
  }, [isReady])

  return (
    <>
      <Box sx={{ maxWidth: { md: 792 } }}>
        <Stack sx={{ gap: 2 }}>
          <Stack sx={{ gap: 2, flexDirection: { xs: 'column', md: 'row' } }}>
            <TextField
              {...useFormMethods.register('searchQuery')}
              data-testid="JobAds__FormTextField__searchFilter"
              fullWidth
              onChange={(e) => {
                void useFormMethods.register('searchQuery').onChange(e)
                storeInputChange({
                  key: 'searchQuery',
                  value: e.target.value || undefined,
                })
              }}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchOutlined />
                    </InputAdornment>
                  ),
                  endAdornment: Boolean(
                    useFormMethods.watch('searchQuery')?.length,
                  ) && (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="clear searchQuery"
                        onClick={() => {
                          useFormMethods.resetField('searchQuery', {
                            defaultValue: '',
                          })
                          storeInputChange({
                            key: 'searchQuery',
                            value: undefined,
                          })
                        }}
                        edge="start"
                        size="small"
                      >
                        <CloseOutlined fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
              name="searchQuery"
              placeholder={t(
                'network.career_website:jobAds.inputs.search.placeholder',
              )}
              sx={{
                maxWidth: { xs: '100%', md: '60%' },
                '& .MuiInputBase-input': {
                  height: '56px',
                  boxSizing: 'border-box',
                },
              }}
            />

            <Box sx={{ width: '100%', maxWidth: { xs: '100%', md: '40%' } }}>
              <Autocomplete
                data-testid="JobAds__Autocomplete__departmentFilter"
                multiple={false}
                onChange={(_, item) => {
                  useFormMethods.setValue(
                    'departmentCodeQuery',
                    item?.value || '',
                  )
                  storeInputChange({
                    key: 'departmentCodeQuery',
                    value: item?.value || undefined,
                  })
                }}
                options={departmentOptions}
                renderInput={(params: AutocompleteRenderInputParams) => (
                  <StyledAutocompleteTextfield
                    {...params}
                    placeholder={t(
                      'main.worker:job_offers.filter.service_placeholder',
                    )}
                    slotProps={{
                      ...params.slotProps,
                      input: {
                        ...params.slotProps.input,
                        startAdornment: (
                          <InputAdornment position="start">
                            <PlaceOutlined />
                          </InputAdornment>
                        ),
                      },
                    }}
                    sx={{
                      '& .MuiInputBase-root': { height: '56px' },
                      '& .MuiInputBase-input': { padding: '0 4px' },
                    }}
                  />
                )}
                value={
                  departmentOptions.find(
                    (dep) =>
                      dep.value ===
                      useFormMethods.getValues('departmentCodeQuery'),
                  ) || null
                }
              />
            </Box>
          </Stack>

          {/* Sorting & Filters*/}
          <Stack
            sx={{
              flexDirection: { xs: 'column', md: 'row' },
              gap: { xs: 2, md: 0 },
              alignItems: { xs: 'start', md: 'center' },
            }}
          >
            <Stack
              sx={{
                flex: 1,
                gap: 1,
                alignItems: 'center',
                flexDirection: 'row',
              }}
            >
              <Box
                sx={{
                  '& .MuiButtonBase-root': {
                    borderRadius: '8px',
                    borderColor: theme.palette.border['brand-default'],
                    '&:hover': { borderColor: 'black' },
                  },
                }}
              >
                <FilterItem
                  dataTestId="JobAds__FilterItem__sort"
                  icon={
                    <SouthOutlined
                      sx={{
                        fontSize: '1rem',
                        transform:
                          useFormMethods.watch('sortOrder') === 'asc'
                            ? 'rotate(180deg)'
                            : 'none',
                        transition: 'transform 0.2s',
                      }}
                    />
                  }
                  label={t('main.worker:job_offers.filter.sort')}
                  onClick={openSortDialog}
                />
              </Box>
              <Box
                sx={{
                  // `&&` outweighs the chip's own class, inserted after this one.
                  '&& > *': {
                    borderRadius: '8px',
                    borderColor: theme.palette.border['brand-default'],
                    '&:hover': { borderColor: 'black' },
                  },
                }}
              >
                <FilterItem
                  dataTestId="JobAds__FilterItem__more_filters"
                  label={t('main.worker:offers.filter.title')}
                  icon={<FilterListOutlined sx={{ fontSize: '1rem' }} />}
                  iconPosition="start"
                  onClick={openMoreFiltersDialog}
                  selected={getMoreFiltersCounter()}
                  onClear={clearMoreFilters}
                  clearAriaLabel={t('main.worker:offers.filter.clear')}
                />
              </Box>
            </Stack>
            <Stack sx={{ flexDirection: 'row', alignItems: 'center' }}>
              <SmallText>Opportunités de remplacement</SmallText>
              <Checkbox
                sx={{ color: theme.palette.text.helper }}
                data-testid="JobOffers__SearchEngine__SwitchItem__replacementFilter"
                checked={storageData.context === 'replacement'}
                onChange={(e) => {
                  const newContext = e.target.checked
                    ? 'replacement'
                    : undefined
                  useFormMethods.setValue('context', newContext)
                  setStorageData({ ...storageData, context: newContext })
                }}
              />
            </Stack>
          </Stack>
        </Stack>

        <FormProvider {...useFormMethods}>
          <ResultsContainer slug={slug} />
        </FormProvider>
      </Box>

      <FormProvider {...useFormMethods}>
        <SortingDialog
          isOpen={sortDialogIsOpen}
          onClose={() => setSortDialogIsOpen(false)}
        />
        <MoreFiltersDialog
          isOpen={moreFiltersDialogIsOpen}
          onClose={() => setMoreFiltersDialogIsOpen(false)}
        />
      </FormProvider>
    </>
  )
}
